/**
 * Dynamic Flapper Component Exports
 * 
 * This file provides easy access to both the logo and loader modes
 * of the DynamicFlapper component.
 */

import React, { useState } from 'react';
import DynamicFlapper from './DynamicFlapper';
import LogoTooltip from './LogoTooltip';
import { getEmotionExplanation } from '../utils/emotionEngine';

/**
 * DynamicFlapperLogo - The main logo component with emotions
 * 
 * @param {Array} matches - Array of match data to calculate emotions and colors
 * @param {String} size - Size of the logo: 'tiny', 'small', 'medium', 'large'
 * @param {Boolean} showTooltip - Whether to show tooltip on hover
 * 
 * @example
 * <DynamicFlapperLogo matches={matches} size="medium" />
 */
export const DynamicFlapperLogo = ({ matches = [], size = 'medium', showTooltip = true, isLoading = false }) => {
  const [tooltipVisible, setTooltipVisible] = useState(false);
  
  const shouldShowTooltip = showTooltip && !isLoading && matches && matches.length > 0;
  
  const explanationText = shouldShowTooltip ? getEmotionExplanation(matches) : null;
  
  const handleClick = () => {
    if (shouldShowTooltip) {
      setTooltipVisible(!tooltipVisible);
    }
  };
  
  const handleMouseEnter = () => {
    if (shouldShowTooltip) {
      setTooltipVisible(true);
    }
  };
  
  const handleMouseLeave = () => {
    if (shouldShowTooltip) {
      setTooltipVisible(false);
    }
  };
  
  return (
    <div 
      style={{ position: 'relative', display: 'inline-block' }}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <DynamicFlapper 
        matches={matches} 
        size={size} 
        showTooltip={showTooltip} 
        mode="logo" 
        isLoading={isLoading} 
      />
      {shouldShowTooltip && explanationText && (
        <LogoTooltip
          text={explanationText}
          isVisible={tooltipVisible}
          onClose={() => setTooltipVisible(false)}
        />
      )}
    </div>
  );
};

/**
 * DynamicFlapperLoader - The loader component with infinite animation
 * 
 * @param {String} size - Size of the loader: 'tiny', 'small', 'medium', 'large'
 * @param {String} loaderBaseColor - Base color for loader flaps (defaults to '#FFF2DD')
 * @param {Number} loaderDarkness1 - First darkness level (0-1, defaults to 0.18)
 * @param {Number} loaderDarkness2 - Second darkness level (0-1, defaults to 0.22)
 * 
 * @example
 * <DynamicFlapperLoader size="medium" />
 * <DynamicFlapperLoader size="medium" loaderBaseColor="#F5F5F5" />
 * <DynamicFlapperLoader size="medium" loaderBaseColor="#FFF2DD" loaderDarkness1={0.15} loaderDarkness2={0.25} />
 */
export const DynamicFlapperLoader = ({ 
  size = 'medium',
  loaderBaseColor = '#FFF2DD',
  loaderDarkness1 = 0.18,
  loaderDarkness2 = 0.22
}) => {
  return (
    <DynamicFlapper 
      size={size} 
      mode="loader" 
      loaderBaseColor={loaderBaseColor}
      loaderDarkness1={loaderDarkness1}
      loaderDarkness2={loaderDarkness2}
    />
  );
};

/**
 * DynamicFlapper - The base component (for advanced usage)
 * 
 * @param {Array} matches - Array of match data
 * @param {String} size - Size: 'tiny', 'small', 'medium', 'large'
 * @param {Boolean} showTooltip - Show tooltip on hover
 * @param {String} mode - Mode: 'logo' or 'loader'
 * 
 * @example
 * <DynamicFlapper matches={matches} size="medium" mode="logo" />
 * <DynamicFlapper size="medium" mode="loader" />
 */
export { default as DynamicFlapper } from './DynamicFlapper';

// Default export for convenience
export default DynamicFlapper;

/**
 * ============================================
 * NEW: Unified Component Exports
 * ============================================
 * These are NEW components that support three modes: loader, logo, qrcode
 * The original components above are UNCHANGED and still work.
 */

import DynamicFlapperUnified from './DynamicFlapperUnified';

/**
 * DynamicFlapperUnifiedLoader - Loader mode wrapper
 * 
 * @param {String} size - Size: 'tiny', 'small', 'medium', 'large'
 * @param {String} loaderBaseColor - Base color (defaults to '#FFF2DD')
 * 
 * @example
 * <DynamicFlapperUnifiedLoader size="medium" />
 */
export const DynamicFlapperUnifiedLoader = ({ 
  size = 'medium',
  loaderBaseColor = '#FFF2DD',
  loaderDarkness1 = 0.10, // 10% darker
  loaderDarkness2 = 0.25  // 25% darker
}) => {
  return (
    <DynamicFlapperUnified 
      mode="loader"
      size={size}
      loaderBaseColor={loaderBaseColor}
      loaderDarkness1={loaderDarkness1}
      loaderDarkness2={loaderDarkness2}
    />
  );
};

/**
 * DynamicFlapperUnifiedLogo - Logo mode wrapper (with tooltip)
 * 
 * @param {Array} matches - Match data for emotion calculation
 * @param {String} size - Size: 'tiny', 'small', 'medium', 'large'
 * @param {Boolean} isLoading - Whether matches are loading
 * @param {Boolean} showTooltip - Show tooltip on hover
 * 
 * @example
 * <DynamicFlapperUnifiedLogo matches={matches} size="medium" />
 */
export const DynamicFlapperUnifiedLogo = ({ 
  matches = [], 
  size = 'medium', 
  showTooltip = true, 
  isLoading = false 
}) => {
  const [tooltipVisible, setTooltipVisible] = useState(false);
  
  const shouldShowTooltip = showTooltip && !isLoading && matches && matches.length > 0;
  const explanationText = shouldShowTooltip ? getEmotionExplanation(matches) : null;
  
  const handleClick = () => {
    if (shouldShowTooltip) {
      setTooltipVisible(!tooltipVisible);
    }
  };
  
  const handleMouseEnter = () => {
    if (shouldShowTooltip) {
      setTooltipVisible(true);
    }
  };
  
  const handleMouseLeave = () => {
    if (shouldShowTooltip) {
      setTooltipVisible(false);
    }
  };
  
  return (
    <div 
      style={{ position: 'relative', display: 'inline-block' }}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <DynamicFlapperUnified 
        mode="logo"
        matches={matches} 
        size={size} 
        showTooltip={showTooltip} 
        isLoading={isLoading} 
      />
      {shouldShowTooltip && explanationText && (
        <LogoTooltip
          text={explanationText}
          isVisible={tooltipVisible}
          onClose={() => setTooltipVisible(false)}
        />
      )}
    </div>
  );
};

/**
 * DynamicFlapperUnifiedQR - QR code mode wrapper
 * 
 * @param {String} matchId - Match ID for QR code URL
 * @param {Object} teamA - Team A data (for overlay text)
 * @param {Object} teamB - Team B data (for overlay text)
 * @param {String} size - Size: 'tiny', 'small', 'medium', 'large'
 * @param {Boolean} isLoading - Whether match data is loading
 * 
 * @example
 * <DynamicFlapperUnifiedQR matchId="abc123" teamA={teamA} teamB={teamB} size="medium" />
 */
export const DynamicFlapperUnifiedQR = ({ 
  matchId, 
  teamA = null,
  teamB = null,
  size = 'medium', 
  isLoading = false 
}) => {
  return (
    <DynamicFlapperUnified 
      mode="qrcode"
      matchId={matchId}
      teamA={teamA}
      teamB={teamB}
      size={size}
      isLoading={isLoading}
    />
  );
};

/**
 * DynamicFlapperUnified - Base unified component (for advanced usage)
 * Export for direct access to all modes
 */
export { DynamicFlapperUnified };
